import { jsx as _jsx } from "react/jsx-runtime";
import { createRoot } from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { Log, WebStorageStateStore } from 'oidc-client-ts';
import App from './components/App';
import OutlookApp from './components/OutlookApp';
import PowerPointApp from './components/PowerPointApp';
import ExcelApp from './components/ExcelApp';
import { HostProvider } from './contexts/HostContext';
import { getOfficeHostType } from './lib/host-adapters';
import { initPolyfills } from './lib/init/polyfills';
import { renderError } from './lib/init/error-renderer';
import { renderEnvironmentSelection } from './lib/init/environment-renderer';
import { bootstrapConfig, hasEnvironmentSelection, isLocalDevelopment, restoreEnvironmentFromUrlParams, } from './lib/service-discovery';
import './index.css';
// Initialize polyfills for Office taskpane context
initPolyfills();
const rootElement = document.getElementById('container');
let root;
if (rootElement) {
    root = createRoot(rootElement);
}
/**
 * Get the redirect URI based on host type
 */
function getRedirectUri(hostType, baseRedirectUri) {
    switch (hostType) {
        case 'outlook':
            return baseRedirectUri.replace('/taskpane.html', '/taskpane-outlook.html');
        case 'powerpoint':
            return baseRedirectUri.replace('/taskpane.html', '/taskpane-powerpoint.html');
        case 'excel':
            return baseRedirectUri.replace('/taskpane.html', '/taskpane-excel.html');
        default:
            return baseRedirectUri;
    }
}
/**
 * Get the appropriate App component based on host type
 */
function getAppComponent(hostType) {
    switch (hostType) {
        case 'outlook':
            return OutlookApp;
        case 'powerpoint':
            return PowerPointApp;
        case 'excel':
            return ExcelApp;
        default:
            return App;
    }
}
/**
 * Render the app with OIDC provider
 */
function renderApp(bootstrapResult, hostType) {
    const { config } = bootstrapResult;
    console.log('[Index] Rendering app with config:', {
        apiUrl: config.apiUrl,
        authority: config.authority,
        source: bootstrapResult.source,
        hostType,
    });
    // Override redirect URI based on host type
    const redirectUri = getRedirectUri(hostType, config.redirectUri);
    const oidcConfig = {
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: redirectUri,
        popup_post_logout_redirect_uri: config.popupPostLogoutRedirectUri,
        userStore: new WebStorageStateStore({ store: window.localStorage }),
    };
    Log.setLogger(console);
    Log.setLevel(Log.DEBUG);
    // Select the appropriate App component based on host type
    const AppComponent = getAppComponent(hostType);
    root?.render(_jsx(AuthProvider, { ...oidcConfig, children: _jsx(HostProvider, { hostType: hostType, children: _jsx(AppComponent, {}) }) }));
}
/**
 * Initialize the application
 */
async function initializeApp() {
    console.log('[Index] Initializing application...');
    // Clear environment selection for localhost development
    if (isLocalDevelopment()) {
        // Keep the local dev environment working as before
        console.log('[Index] Local development detected');
    }
    // Restore environment from URL params BEFORE checking hasEnvironmentSelection
    // This is critical for cross-origin redirects where localStorage is empty on new origin
    restoreEnvironmentFromUrlParams();
    // Check if we need to show environment selection first
    if (!hasEnvironmentSelection() && !isLocalDevelopment()) {
        console.log('[Index] No environment selected, waiting for selection');
        // Wait for Office.js to be ready to detect host type
        const info = await new Promise((resolve) => Office.onReady(resolve));
        const hostType = getOfficeHostType(info.host);
        // Render without auth - show environment selection
        if (root) {
            renderEnvironmentSelection(root, hostType);
        }
        return;
    }
    // Bootstrap configuration with service discovery
    const bootstrapResult = await bootstrapConfig();
    // If bootstrapConfig returns null, a redirect is in progress
    if (!bootstrapResult) {
        console.log('[Index] Bootstrap returned null (redirect in progress or no env selected)');
        // If we're here without a redirect, show environment selection
        if (hasEnvironmentSelection()) {
            // Redirect is in progress, don't render anything
            return;
        }
        // Wait for Office.js to be ready to detect host type
        const info = await new Promise((resolve) => Office.onReady(resolve));
        const hostType = getOfficeHostType(info.host);
        if (root) {
            renderEnvironmentSelection(root, hostType);
        }
        return;
    }
    // Wait for Office.js to be ready
    const info = await new Promise((resolve) => Office.onReady(resolve));
    const hostType = getOfficeHostType(info.host);
    console.log('[Index] Detected Office host:', info.host, '-> hostType:', hostType);
    // Handle redirect from OIDC provider - if we're on taskpane.html but in Outlook, redirect to taskpane-outlook.html
    // This ensures the OIDC redirect URI matches the expected URL
    if (window.location.pathname === '/taskpane.html' && hostType === 'outlook') {
        const newUrl = window.location.href.replace('/taskpane.html', '/taskpane-outlook.html');
        console.log('[Index] Redirecting Outlook from taskpane.html to taskpane-outlook.html');
        window.location.href = newUrl;
        return;
    }
    // Handle redirect from OIDC provider - if we're on taskpane.html but in PowerPoint, redirect to taskpane-powerpoint.html
    if (window.location.pathname === '/taskpane.html' && hostType === 'powerpoint') {
        const newUrl = window.location.href.replace('/taskpane.html', '/taskpane-powerpoint.html');
        console.log('[Index] Redirecting PowerPoint from taskpane.html to taskpane-powerpoint.html');
        window.location.href = newUrl;
        return;
    }
    // Handle redirect from OIDC provider - if we're on taskpane.html but in Excel, redirect to taskpane-excel.html
    if (window.location.pathname === '/taskpane.html' && hostType === 'excel') {
        const newUrl = window.location.href.replace('/taskpane.html', '/taskpane-excel.html');
        console.log('[Index] Redirecting Excel from taskpane.html to taskpane-excel.html');
        window.location.href = newUrl;
        return;
    }
    // Render the app with the bootstrapped config
    renderApp(bootstrapResult, hostType);
}
// Start initialization
initializeApp().catch((error) => {
    console.error('[Index] Failed to initialize application:', error);
    // Show error state to the user
    Office.onReady(() => {
        if (rootElement) {
            renderError(rootElement, error instanceof Error ? error : new Error(String(error)));
        }
    });
});
if (module.hot) {
    ;
    module.hot.accept('./components/App', () => {
        const NextApp = require('./components/App').default;
        root?.render(NextApp);
    });
}
